import { useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { usePublicHolidays } from '../../hooks/useLeave';
import { toDateKey } from '../../lib/calendarDates';
import type { LeaveRequest, LeaveType } from '../../lib/leaveApi';
import {
  LEAVE_TYPES,
  countWorkingDays,
  holidayKeySet,
  yearsInRange,
} from '../../lib/leaveConstants';
import { DateField } from '../ui/DateField';
import { Select } from '../ui/Select';

export interface LeaveFormValues {
  /** Only ever set when filing on someone else's behalf (`leave.canManage`). */
  userId?: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  hours: number | null;
  reason: string | null;
}

/** An hours-long absence is capped at a working day; half hours are allowed. */
const MIN_HOURS = 0.5;
const MAX_HOURS = 12;

/**
 * Create / edit a leave request, modelled on `ReminderForm`: an inline card,
 * no modal, cancel and save at the bottom right.
 *
 * The cost in working days is computed here instead of waiting for the server
 * to answer, because the whole decision a person makes in this form ("can I
 * afford these days?") depends on the number — and weekends plus the Romanian
 * legal holidays are free. The holiday table is fetched for the years the
 * range spans rather than hardcoded, so there is exactly one calendar in the
 * system (see the note in `leaveConstants`).
 */
export function LeaveForm({
  request,
  people,
  busy,
  onSave,
  onCancel,
}: {
  /** Present when editing an existing request. */
  request?: LeaveRequest;
  /** Team members to file for. Omit it and the form is always "for me". */
  people?: { id: string; name: string }[];
  busy?: boolean;
  onSave: (values: LeaveFormValues) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const today = toDateKey(new Date());

  const [userId, setUserId] = useState(request?.userId ?? '');
  const [type, setType] = useState<LeaveType>(request?.type ?? 'ANNUAL');
  const [startDate, setStartDate] = useState(request?.startDate ?? today);
  const [endDate, setEndDate] = useState(request?.endDate ?? today);
  const [hours, setHours] = useState(request?.hours != null ? String(request.hours) : '4');
  const [reason, setReason] = useState(request?.reason ?? '');

  const isShort = type === 'SHORT_ABSENCE';
  // An hours-long absence is one day by definition, so the range always
  // collapses onto the start date — the end field is not even rendered.
  const effectiveEnd = isShort ? startDate : endDate;

  const { year, through } = yearsInRange(startDate, effectiveEnd);
  const { data: holidays } = usePublicHolidays(year, through);
  const holidayKeys = useMemo(() => holidayKeySet(holidays ?? []), [holidays]);
  const workingDays = countWorkingDays(startDate, effectiveEnd, holidayKeys);

  const hoursNum = Number(hours);
  const hoursValid =
    !isShort || (Number.isFinite(hoursNum) && hoursNum >= MIN_HOURS && hoursNum <= MAX_HOURS);
  const rangeValid = startDate !== '' && effectiveEnd >= startDate;
  // A request that costs nothing (a weekend, a public holiday) is never what
  // the person meant, and the server rejects it anyway.
  const valid = rangeValid && workingDays > 0 && hoursValid;

  function save() {
    if (!valid || busy) return;
    onSave({
      // Editing never moves a request to another person, so `userId` is only
      // ever sent on create.
      ...(!request && userId ? { userId } : {}),
      type,
      startDate,
      endDate: effectiveEnd,
      hours: isShort ? hoursNum : null,
      reason: reason.trim() || null,
    });
  }

  const costLine = !rangeValid
    ? t('leave.costNone')
    : isShort
      ? t('leave.costHours', { count: hours })
      : workingDays === 0
        ? t('leave.costNone')
        : workingDays === 1
          ? t('leave.costOne')
          : t('leave.cost', { count: workingDays });

  return (
    <div
      className="flex flex-col gap-3 px-4 py-3"
      // Escape closes the form. Enter is deliberately NOT a save shortcut here
      // (unlike ReminderForm): the reason is a textarea and both date fields
      // open a popover whose own Enter selects a day.
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      {!request && people && people.length > 0 && (
        <Select
          label={t('leave.field.person')}
          value={userId}
          onChange={setUserId}
          clearable
          options={people.map((p) => ({ value: p.id, label: p.name }))}
          placeholder={t('leave.filter.all')}
          className="sm:max-w-xs"
        />
      )}

      <Select
        label={t('leave.field.type')}
        value={type}
        onChange={(next) => setType(next as LeaveType)}
        options={LEAVE_TYPES.map((value) => ({ value, label: t(`leave.type.${value}`) }))}
        required
        className="sm:max-w-xs"
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <span className="ui-field-label">
            {isShort ? t('leave.field.day') : t('leave.field.start')}
          </span>
          <DateField
            value={startDate}
            onChange={(next) => {
              setStartDate(next);
              // Keep the range sane when the start jumps past the end.
              if (endDate < next) setEndDate(next);
            }}
            required
          />
        </div>

        {!isShort && (
          <div className="w-40">
            <span className="ui-field-label">{t('leave.field.end')}</span>
            <DateField
              value={endDate}
              onChange={setEndDate}
              min={startDate}
              required
              aria-invalid={!rangeValid}
            />
          </div>
        )}

        {isShort && (
          <div className="w-28">
            <span className="ui-field-label">{t('leave.field.hours')}</span>
            <input
              type="number"
              min={MIN_HOURS}
              max={MAX_HOURS}
              step={0.5}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              aria-invalid={!hoursValid}
              className={`ui-input ${hoursValid ? '' : 'border-red-500'}`}
            />
          </div>
        )}
      </div>

      {/* The live cost, right under the dates it is computed from. */}
      <div>
        <p
          className={`text-sm font-medium ${valid ? 'text-foreground' : 'text-red-600'}`}
          aria-live="polite"
        >
          {costLine}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('leave.costHint')}</p>
      </div>

      <div>
        <span className="ui-field-label">{t('leave.field.reason')}</span>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('leave.field.reasonPlaceholder')}
          className="ui-textarea"
        />
      </div>

      <div className="mt-1 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="ui-btn-secondary">
          {t('leave.cancel')}
        </button>
        <button type="button" disabled={!valid || busy} onClick={save} className="ui-btn-primary">
          {request ? t('leave.saveEdit') : t('leave.save')}
        </button>
      </div>
    </div>
  );
}
